// Tiering. The control sits on the volume, not on the rate.
//
// One card, and the readout IS the control: the Leads bar shows where the tier
// boundaries fall and the handles on it are how they move. Splitting the two
// produced a read-only copy of the same bar above a second interactive one, which
// is two pictures of one fact.
//
// The Conversions bar underneath is the other half of the reading, and the pair
// is the point. A tier holding most of the conversions may be doing so only by
// holding most of the leads, which is visible as the two bars lining up rather
// than as a number anywhere. The headline states the one comparison worth
// reading without hunting: what share of leads the qualified tiers hold against
// what share of conversions they carry.
//
// Handles are drawn where the boundaries ACTUALLY fall. A request that had to
// snap to the nearest score shows where it landed, while the request itself stays
// in the page's state so a later weight change re-derives from what was asked
// for rather than from what was settled for.
import React from 'react';
import { observer } from 'mobx-react';

import DraftNumberInput from './DraftNumberInput';
import {
  BOUNDED_TIERS,
  LadderRung,
  rangeHint,
  nearestShare,
  shareLabels,
  thresholdRange,
  TierOutcome,
} from './scoreLadder';
import './tieringPanel.css';

/** Below this the band is narrower than its own name, or its own number. */
const MIN_NAME = 0.1;
const MIN_VALUE = 0.05;

/**
 * Past this many rungs the tick strip is a solid grey block, which says only
 * "dense" and costs a row of the card to say it.
 */
const MAX_TICKS = 48;

/**
 * Closer than this, two boundary fields would overlap on one row, so the later
 * one drops to a second row. A share of the bar, sized for a four digit total.
 */
const FIELD_GAP = 0.14;

const rate = (value: number) => `${value.toFixed(1)}%`;
const count = (value: number) => value.toLocaleString('en-US');

/**
 * The row each boundary field goes on: the first row where it clears the last
 * field already placed there. Compared only with its neighbour, the first and
 * third of three close handles both took row 0 and overlapped.
 */
export function fieldRowsFor(edges: number[], gap: number): number[] {
  const lastOnRow: number[] = [];
  return edges.map((edge) => {
    const row = lastOnRow.findIndex((last) => edge - last >= gap);
    const placed = row === -1 ? lastOnRow.length : row;
    lastOnRow[placed] = edge;
    return placed;
  });
}

function TieringPanel({
  tiers,
  bounds,
  ladder,
  onChangeBounds,
  disabled,
  thresholdInput,
}: {
  /** Best first, four of them, as produced by the thresholds now in the model. */
  tiers: TierOutcome[];
  /**
   * The requested cumulative shares. Carried in so a drag rewrites only the
   * boundary it moved and clamps against the other requests, rather than against
   * where they happen to have landed. Rebuilding the set from achieved values is
   * how the prototype's tiers drifted away from the request.
   */
  bounds: number[];
  ladder: LadderRung[];
  onChangeBounds: (bounds: number[]) => void;
  disabled?: boolean;
  /**
   * A typed threshold under each handle. Offered where the score is a number a
   * person reasons in, which is a point total; on a tree based model the handle
   * is the control and the threshold is its consequence, so there is none.
   */
  thresholdInput?: {
    step: number;
    suffix: string;
    onChange: (segment: string, value: number) => void;
  };
}) {
  const barRef = React.useRef<HTMLDivElement>(null);

  // Largest remainder, so the labels on each bar total 100 and never read as 99.
  const leadLabels = shareLabels(tiers.map((tier) => tier.population));
  const conversionLabels = shareLabels(tiers.map((tier) => tier.conversions));

  // Cumulative achieved share at each boundary. Four tiers, three boundaries.
  const edges = tiers
    .slice(0, -1)
    .map((tier, index) =>
      tiers.slice(0, index + 1).reduce((sum, each) => sum + each.share, 0)
    );

  // The thresholds as the tiers carry them, which is what the fields show and
  // what each field's range is taken against.
  const typed = BOUNDED_TIERS.reduce((out, segment, index) => {
    out[segment] = Number(tiers[index]?.threshold);
    return out;
  }, {} as Record<string, number>);
  const fieldRows = fieldRowsFor(edges, FIELD_GAP);
  // The field holding back what is typed, so it can say why.
  const [refused, setRefused] = React.useState<string | null>(null);

  const move = (index: number, target: number) => {
    const clamped = Math.max(0, Math.min(1, target));
    // Against the neighbouring REQUESTS, so a boundary cannot cross one that has
    // not been re-requested, and strictly inside them so two can never stack.
    const landed = nearestShare(
      clamped,
      ladder,
      index > 0 ? bounds[index - 1] : 0,
      index < bounds.length - 1 ? bounds[index + 1] : 1
    );
    // No lead sits in the gap, so the handle holds rather than snapping
    // somewhere it was not dragged to.
    if (landed === null || landed === bounds[index]) return;

    const next = bounds.slice();
    next[index] = landed;
    onChangeBounds(next);
  };

  /** Arrow keys step to the adjacent rung, not by a fixed percentage. The rungs
   *  are what a boundary may occupy, so a step of anything else would either
   *  skip reachable splits or land back where it started. */
  const step = (index: number, direction: number) => {
    const ordered = ladder.map((rung) => rung.share).sort((a, b) => a - b);
    const at = ordered.findIndex(
      (share) => Math.abs(share - edges[index]) < 1e-9
    );
    const next = ordered[at + direction];
    if (next != null) move(index, next);
  };

  const shareAt = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect?.width) return 0;
    return (clientX - rect.left) / rect.width;
  };

  return (
    <div className="tier-grid">
      <div className="tier-main">
        {/* Read off the labels the bars below actually print, not off a second
            rounding of the same shares. Summing the exact values and rounding
            once said "81% of conversions" over a bar reading 48 and 32. */}
        <p className="tier-head">
          <b>{leadLabels[0] + leadLabels[1]}%</b> of leads (
          {tiers[0].label.toLowerCase()} and {tiers[1].label.toLowerCase()})
          make <b>{conversionLabels[0] + conversionLabels[1]}%</b> of
          conversions.
        </p>

        <p className="tier-barlab">Leads</p>
        <div className="tier-wrap">
          <div className="tier-bar" ref={barRef}>
            {tiers.map((tier, index) => (
              <div
                key={tier.segment}
                className="tier-seg"
                style={{
                  width: `${tier.share * 100}%`,
                  background: tier.color,
                }}
                title={`${tier.label}: ${count(tier.population)} leads, ${
                  leadLabels[index]
                }% of the population, converting at ${rate(tier.rate)}`}
              >
                {tier.share > MIN_NAME && (
                  <span
                    className="tier-seg-name"
                    style={{ color: tier.textColor }}
                  >
                    {tier.label}
                  </span>
                )}
                {tier.share > MIN_VALUE && (
                  <span
                    className="tier-seg-value"
                    style={{ color: tier.textColor }}
                  >
                    {leadLabels[index]}%
                  </span>
                )}
              </div>
            ))}
          </div>

          {edges.map((edge, index) => (
            <button
              type="button"
              key={`edge_${tiers[index].segment}`}
              className="tier-handle"
              style={{ left: `${edge * 100}%` }}
              role="slider"
              aria-label={`${tiers[index].label} boundary`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(edge * 100)}
              aria-valuetext={`${rate(edge * 100)} of leads above this point`}
              disabled={disabled}
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
              onPointerMove={(event) => {
                if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
                  return;
                }
                move(index, shareAt(event.clientX));
              }}
              onPointerUp={(event) => {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }}
              onKeyDown={(event) => {
                const direction =
                  event.key === 'ArrowLeft'
                    ? -1
                    : event.key === 'ArrowRight'
                    ? 1
                    : 0;
                if (!direction) return;
                event.preventDefault();
                step(index, direction);
              }}
            />
          ))}
        </div>

        {/* Every score a boundary can occupy. This is the difference between a
            handle that slides and one that steps, and without it the snapping
            reads as the control fighting the pointer. */}
        <div className="tier-ticks">
          {ladder.length <= MAX_TICKS &&
            ladder.map((rung) => (
              <span
                key={rung.score}
                className="tier-tick"
                style={{ left: `${rung.share * 100}%` }}
              />
            ))}
        </div>
        {thresholdInput && (
          <div
            className="tier-fields"
            style={{ height: `${(Math.max(0, ...fieldRows) + 1) * 32}px` }}
          >
            {edges.map((edge, index) => {
              const segment = BOUNDED_TIERS[index];
              const range = thresholdRange(segment, typed, thresholdInput.step);
              const value = tiers[index]?.threshold;
              return (
                <label
                  key={`field_${segment}`}
                  className="tier-field"
                  style={{
                    left: `${edge * 100}%`,
                    top: `${fieldRows[index] * 32}px`,
                  }}
                >
                  <DraftNumberInput
                    step={thresholdInput.step}
                    min={range.min}
                    max={range.max}
                    value={value}
                    disabled={disabled}
                    aria-label={`${tiers[index].label} threshold`}
                    data-testid={`tiering-threshold-${segment.replace(
                      ' ',
                      '-'
                    )}`}
                    onCommit={(next) => thresholdInput.onChange(segment, next)}
                    onRefusedChange={(isRefused) =>
                      setRefused((current) =>
                        isRefused
                          ? segment
                          : current === segment
                          ? null
                          : current
                      )
                    }
                  />
                  <span>{thresholdInput.suffix}</span>
                  {refused === segment && (
                    <span className="tier-field-hint" role="status">
                      {rangeHint(segment, typed)}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        <div className="tier-ends">
          <span>Best converting</span>
          <span>Worst converting</span>
        </div>

        <p className="tier-barlab tier-barlab--spaced">Conversions</p>
        <div className="tier-convbar">
          {tiers.map((tier, index) => (
            <div
              key={tier.segment}
              className="tier-convseg"
              style={{
                width: `${tier.conversionShare * 100}%`,
                background: tier.color,
              }}
              title={`${tier.label}: ${count(tier.conversions)} conversions, ${
                conversionLabels[index]
              }% of all conversions`}
            >
              {tier.conversionShare > MIN_VALUE && (
                <span
                  className="tier-seg-value"
                  style={{ color: tier.textColor }}
                >
                  {conversionLabels[index]}%
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default observer(TieringPanel);
